/**
 * The composer's staging area for image / PDF / Excel attachments.
 *
 * Optimistic, the way the reference is: a chip appears the instant a file is
 * picked, showing a local preview from a blob URL, and the S3 upload runs behind
 * it. The alternative — blocking the composer until the upload finishes — makes
 * attaching a 5 MB PDF feel like the app hung.
 *
 * Three things this has to get right, all of them about not leaving rubbish
 * behind or sending rubbish forward:
 *
 *   - **Only `ready` chips are sent.** One still uploading has a *blob* URL,
 *     which means nothing to the agent; one that failed has no URL at all.
 *   - **Removing a chip aborts its upload**, so a cancelled file does not finish
 *     landing in S3 with nothing pointing at it.
 *   - **Every blob URL is revoked** — on remove, on clear, and on unmount.
 *     `URL.createObjectURL` leaks the whole file until the page goes away.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../lib/config";
import {
  AttachmentValidationError,
  MAX_ATTACHMENT_MB,
  detectFileKind,
  localDrawingFromPath,
  uploadAttachment,
  type Attachment,
  type AttachmentKind,
} from "../lib/attachments";

export interface StagedAttachment {
  id: string;
  filename: string;
  size: number;
  kind: AttachmentKind;
  status: "uploading" | "ready" | "failed";
  /** The S3 URL once ready; a local blob URL before that. Empty for a local DWG. */
  url: string;
  /** A DWG's real path. It is not an upload. */
  localPath?: string;
  /** Tracked apart from `url` so it can still be revoked after the swap. */
  localBlobUrl?: string;
  controller?: AbortController;
}

let counter = 0;

export function useAttachments(token: string | null, options?: { localDrawings?: boolean }): {
  staged: StagedAttachment[];
  /** Validation failures, in the user's words. */
  error: string | null;
  add: (files: FileList | File[]) => void;
  remove: (id: string) => void;
  clear: () => void;
  /** What may actually be sent — `ready` only. */
  sendable: () => Attachment[];
} {
  const [staged, setStaged] = useState<StagedAttachment[]>([]);
  const [error, setError] = useState<string | null>(null);

  const tokenRef = useRef(token);
  tokenRef.current = token;
  /** Read on unmount, where the state value would be the stale first render. */
  const stagedRef = useRef<StagedAttachment[]>([]);
  stagedRef.current = staged;

  useEffect(
    () => () => {
      for (const a of stagedRef.current) {
        a.controller?.abort();
        if (a.localBlobUrl) URL.revokeObjectURL(a.localBlobUrl);
      }
    },
    [],
  );

  const localDrawings = options?.localDrawings === true;
  const add = useCallback((files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setError(null);

    for (const file of list) {
      const kind = detectFileKind(file);
      // Validated per file rather than as a batch: one unsupported file should
      // not throw away the three good ones picked with it.
      if (!kind || (kind === "dwg" && !localDrawings)) {
        setError(`Unsupported file type: ${file.name}`);
        continue;
      }
      if (kind !== "dwg" && file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
        setError(`File "${file.name}" exceeds the ${MAX_ATTACHMENT_MB} MB limit.`);
        continue;
      }

      counter += 1;
      const id = `att-${counter}`;
      if (kind === "dwg") {
        const path = window.bimgo?.files?.pathFor(file) ?? "";
        const drawing = localDrawingFromPath(file, path);
        if (!drawing) {
          setError(`Could not read the location of "${file.name}". Choose it again in the desktop app.`);
          continue;
        }
        setStaged((prev) => [
          ...prev,
          { id, filename: file.name, size: file.size, kind, status: "ready", url: "", localPath: drawing.localPath },
        ]);
        continue;
      }

      const blobUrl = URL.createObjectURL(file);
      const controller = new AbortController();

      setStaged((prev) => [
        ...prev,
        {
          id,
          filename: file.name,
          size: file.size,
          kind,
          status: "uploading",
          url: blobUrl,
          localBlobUrl: blobUrl,
          controller,
        },
      ]);

      void uploadAttachment(file, tokenRef.current, {
        apiBaseUrl: API_BASE_URL(),
        signal: controller.signal,
      })
        .then((uploaded) => {
          setStaged((prev) =>
            prev.map((a) =>
              a.id === id ? { ...a, status: "ready", url: uploaded.url } : a,
            ),
          );
        })
        .catch((e: unknown) => {
          // An abort is the user removing the chip; the entry is already gone.
          if (e instanceof Error && e.name === "AbortError") return;
          if (e instanceof AttachmentValidationError) setError(e.message);
          setStaged((prev) =>
            prev.map((a) => (a.id === id ? { ...a, status: "failed" } : a)),
          );
        });
    }
  }, [localDrawings]);

  const remove = useCallback((id: string) => {
    setStaged((prev) => {
      const found = prev.find((a) => a.id === id);
      found?.controller?.abort();
      if (found?.localBlobUrl) URL.revokeObjectURL(found.localBlobUrl);
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const clear = useCallback(() => {
    setStaged((prev) => {
      for (const a of prev) {
        a.controller?.abort();
        if (a.localBlobUrl) URL.revokeObjectURL(a.localBlobUrl);
      }
      return [];
    });
    setError(null);
  }, []);

  const sendable = useCallback(
    (): Attachment[] =>
      stagedRef.current
        .filter((a) => a.status === "ready")
        .map((a) => ({
          url: a.url,
          kind: a.kind,
          filename: a.filename,
          size: a.size,
          ...(a.localPath ? { localPath: a.localPath } : {}),
        })),
    [],
  );

  return { staged, error, add, remove, clear, sendable };
}
